import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import {
  faBath, faBed, faCar,
} from '@fortawesome/pro-light-svg-icons'

import { DateTime } from 'luxon'

import {
  Box, Image, Text,
} from '@domain-group/suit-theme'

import useAttachment from '@hooks/useAttachment'
import type { UseCampaignPayload } from '@hooks/useCampaign'
import useCampaignSaleMethods from '@hooks/useCampaignSaleMethods'
import useCurrentUser from '@hooks/useCurrentUser'

import LoggedInUser from '@components/LoggedInUser'

import { CampaignSaleMethodModel } from '@models/types'

import DefaultImage from './assets/default.jpg'

interface HeaderProps {
  campaignPayload: UseCampaignPayload,
  coverImageId: number,
}

const Header = (props: HeaderProps) => {
  const { campaignPayload, coverImageId } = props

  const {
    campaign,
    campaign: {
      bedrooms,
      bathrooms,
      campaign_sale_method_id,
      carparks,
      car_spaces,
      carports,
      title,
    },
    loading,
  } = campaignPayload

  const { currentUser } = useCurrentUser()

  const brandCoverImageAttachment = useAttachment({ id: coverImageId })
  const brandCoverImageUrl = brandCoverImageAttachment?.attachment?.links?.original_url || ''

  const campaignSaleMethodPayload = useCampaignSaleMethods({})
  const { filteredCampaignSaleMethods } = campaignSaleMethodPayload
  const campaignSaleMethod = filteredCampaignSaleMethods.find(
    (method: CampaignSaleMethodModel) => method.id === campaign_sale_method_id,
  )?.title ?? ''

  const auctionDate = (campaignSaleMethod === 'Auction' && campaign?.auction_date?.length)
    ? DateTime.fromISO(campaign?.auction_date).toLocaleString(DateTime.DATE_FULL) : ''

  const campaignCategory = campaignPayload?.campaignCategory?.title || ''

  const cars = Number(car_spaces ?? 0) + Number(carports ?? 0) + Number(carparks ?? 0)
  const campaignDetails = [
    {
      icon: faBed,
      text: bedrooms,
    },
    {
      icon: faBath,
      text: bathrooms,
    },
    {
      icon: faCar,
      text: !Number.isNaN(cars) ? cars : '',
    },
  ]

  return (
    <Box flexDirection="column">
      <Image
        boxProps={{
          flexDirection: 'column', position: 'relative', zIndex: 1,
        }}
        height={[350, 450]}
        placeholderUrl={DefaultImage}
        url={brandCoverImageUrl}
      >
        <Box
          backgroundColor="rgba(0, 0, 0, 0.7)"
          position="absolute"
          top={0}
          right={0}
          bottom={0}
          left={0}
        />

        <Box position="absolute" top={0} left={0}>
          <Box
            alignItems="center"
            height="fit-content"
            justifyContent="space-between"
            paddingLeft="large"
            paddingY="medium"
            position="relative"
          >
            <Box flex={1} justifyContent="end">
              {currentUser?.id && (
                <LoggedInUser
                  boxProps={{
                    display: ['none', 'flex'], flex: 1, justifyContent: 'end',
                  }}
                  darkMode
                  isPublicRoute
                />
              )}
            </Box>
          </Box>
        </Box>

        {!loading && (
          <Box
            alignItems="start"
            flexDirection="column"
            justifyContent="start"
            marginTop={currentUser?.id ? 'xxlarge' : 'large'}
            maxWidth="calc(100% - 64px)"
            paddingTop={[0, 'large']}
            paddingX={[0, 0]}
            width={['100%', '900px']}
            zIndex={2}
          >
            <Text
              color="white"
              fontSize={['28px', '36px']}
              fontWeight={500}
              lineHeight={1.3}
              textAlign="left"
              style={{
                letterSpacing: '1px',
              }}
            >
              {title}
            </Text>

            <Box
              display="flex"
              // @ts-expect-error TS2322
              gap={1}
              width="fit-content"
            >
              {campaignDetails.map((detail) => {
                const text = detail?.text ? detail.text : ' - '
                return (
                  <Box marginRight="large">
                    <Text
                      color="white"
                      fontSize={['medium', 'xlarge']}
                      lineHeight={1.5}
                      textAlign="left"
                      marginRight="small"
                    >
                      {/*
                       // @ts-expect-error TS2322 */}
                      <FontAwesomeIcon fontSize={['medium', 'xlarge']} icon={detail.icon} />
                    </Text>

                    <Text color="white" fontSize={['medium', 'xlarge']} marginLeft="small" marginY="auto">{text}</Text>
                  </Box>
                )
              })}

              <Text fontSize={['medium', 'xlarge']} color="white" lineHeight={1.5} textAlign="left">
                {campaignCategory}
              </Text>
            </Box>

            <Box marginTop="medium">
              <Text
                fontSize={['medium', 'large']}
                color="white"
                marginLeft="xxsmall"
                marginY="auto"
              >
                {campaignSaleMethod} {auctionDate}
              </Text>
            </Box>
          </Box>
        )}
      </Image>
    </Box>
  )
}

export default Header
